import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import "./ComitesEditor.css";

const normalizeComites = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => ({
    titulo: typeof item?.titulo === "string" ? item.titulo : "",
    descripcion: typeof item?.descripcion === "string" ? item.descripcion : "",
  }));
};

function ComitesEditor({ value, onSave }) {
  const [comites, setComites] = useState(() => normalizeComites(value));
  const [warning, setWarning] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setComites(normalizeComites(value));
  }, [value]);

  const handleChange = (index, field, fieldValue) => {
    setComites((prev) =>
      prev.map((comite, i) =>
        i === index
          ? {
              ...comite,
              [field]: fieldValue,
            }
          : comite
      )
    );
  };

  const handleRemove = (index) => {
    setComites((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    setComites((prev) => [...prev, { titulo: "", descripcion: "" }]);
  };

  const handleSave = async () => {
    setWarning("");
    setError("");

    const hasInvalidTitulo = comites.some((comite) => !comite.titulo.trim());
    if (hasInvalidTitulo) {
      setWarning("Todos los comités deben tener un nombre antes de guardar.");
      return;
    }

    try {
      setSaving(true);
      await onSave(comites);
    } catch (saveError) {
      setError("No se pudo guardar la estructura de comités.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="comites-editor">
      <div className="comites-editor-header">
        <Typography variant="h6">Comités Técnicos</Typography>
        <p className="comites-editor-subtitle">
          Agregá los comités que aparecerán en el sitio. Completá el nombre y la
          descripción de cada uno.
        </p>
      </div>

      {warning && <Alert severity="warning">{warning}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      <div className="comites-editor-list">
        {comites.map((comite, index) => (
          <article key={`comite-${index}`} className="comites-editor-card">
            <div className="comites-editor-fields">
              <TextField
                fullWidth
                label="Nombre del comité"
                value={comite.titulo}
                onChange={(event) => handleChange(index, "titulo", event.target.value)}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descripción"
                value={comite.descripcion}
                onChange={(event) =>
                  handleChange(index, "descripcion", event.target.value)
                }
              />
            </div>

            <div className="comites-editor-remove">
              <IconButton
                aria-label={`Eliminar comité ${index + 1}`}
                onClick={() => handleRemove(index)}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          </article>
        ))}
      </div>

      <div className="comites-editor-actions">
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
          Agregar comité
        </Button>
      </div>

      <div className="comites-editor-actions">
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : "Guardar comités"}
        </Button>
      </div>
    </div>
  );
}

export default ComitesEditor;
